import { useRef, useState } from "react";
import { Phone, MessageCircle, BadgeCheck, Loader2, ImagePlus, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/common";
import { OUTCOMES, OUTCOME } from "@/components/leads/leadMeta";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, waLink } from "@/lib/format";

export const CallLog = ({ lead, onSaved }) => {
  const [f, setF] = useState({ outcome: "", note: "", follow_up: "" });
  const save = async () => {
    try { const r = await api.post(`/leads/${lead.id}/call`, f); toast.success("Call logged"); setF({ outcome: "", note: "", follow_up: "" }); onSaved(r.data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-[#E7E2D6] bg-[#FBF9F4] p-4" data-testid="lead-call-form">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Log this call</div>
      <div className="grid grid-cols-2 gap-2">
        {OUTCOMES.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setF({ ...f, outcome: k })} data-testid={`lead-outcome-${k}`}
            className={`rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${f.outcome === k ? "border-[#0F382C] bg-[#0F382C] text-white" : "border-[#E7E2D6] bg-white hover:border-[#0F382C]/40"}`}>{l}</button>
        ))}
      </div>
      <Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="What did they say?" rows={2} data-testid="lead-call-note" />
      <div className="flex items-center gap-2"><span className="whitespace-nowrap text-xs text-muted-foreground">Follow up on</span><Input type="date" value={f.follow_up} onChange={(e) => setF({ ...f, follow_up: e.target.value })} className="h-9" data-testid="lead-follow-up" /></div>
      <Button disabled={!f.outcome} onClick={save} className="w-full rounded-full bg-[#0F382C] hover:bg-[#134E3A]" data-testid="lead-call-save">Save call</Button>
      {(lead.calls || []).length > 0 && (
        <div className="space-y-2 border-t border-[#E7E2D6] pt-3" data-testid="lead-call-history">
          {[...lead.calls].reverse().map((c, i) => (
            <div key={i} className="text-xs"><span className="font-semibold">{OUTCOME[c.outcome]}</span> · {c.by} · {fmtDateTime(c.at)}{c.follow_up && ` · follow up ${c.follow_up}`}{c.note && <div className="text-muted-foreground">{c.note}</div>}</div>
          ))}
        </div>
      )}
    </div>
  );
};

const VIA = { razorpay: "paid online (Razorpay)", upi: "UPI, screenshot uploaded", cash: "cash, receipt photo uploaded" };

const MemberInfo = ({ lead }) => {
  const view = async () => {
    try { const r = await api.get(`/leads/${lead.id}/proof`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch (e) { toast.error("Could not open the screenshot"); }
  };
  const p = lead.member_proof;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="lead-member-info">
      <BadgeCheck className="h-4 w-4" /><span className="flex-1">Member{lead.member_at ? ` since ${fmtDateTime(lead.member_at)}` : ""} · {VIA[lead.member_via] || "membership paid"}{p ? ` by ${p.by}` : ""}</span>
      {p && <button onClick={view} className="flex items-center gap-1 font-semibold underline-offset-2 hover:underline" data-testid="lead-view-proof-btn"><Eye className="h-4 w-4" />View screenshot</button>}
    </div>
  );
};

const MarkPaid = ({ lead, onSaved }) => {
  const ref = useRef(null);
  const [mode, setMode] = useState("upi");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const paid = async () => {
    const fd = new FormData(); fd.append("mode", mode); fd.append("file", file);
    setBusy(true);
    try { const r = await api.post(`/leads/${lead.id}/mark-paid`, fd); toast.success(`${lead.name} is now a member`); onSaved(r.data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 rounded-2xl border border-[#E7E2D6] p-3" data-testid="lead-mark-paid">
      <div className="text-xs text-muted-foreground">Collected the membership yourself? Upload the payment screenshot to make them a member. Online Razorpay payments convert automatically.</div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,.heic" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="lead-proof-file" />
      <div className="flex gap-2">
        <NativeSelect className="w-28" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="lead-paid-mode"><option value="upi">UPI</option><option value="cash">Cash</option></NativeSelect>
        <Button type="button" variant="outline" onClick={() => ref.current?.click()} className="min-w-0 flex-1 rounded-full" data-testid="lead-proof-pick"><ImagePlus className="mr-1 h-4 w-4 shrink-0" /><span className="truncate">{file ? file.name : "Payment screenshot"}</span></Button>
      </div>
      <Button disabled={busy || !file} onClick={paid} variant="outline" className="w-full rounded-full border-emerald-600 text-emerald-700 hover:bg-emerald-50" data-testid="lead-mark-paid-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><BadgeCheck className="mr-1 h-4 w-4" />Mark membership paid</>}</Button>
    </div>
  );
};

export const LeadActions = ({ lead, onSaved }) => {
  const done = ["member", "customer"].includes(lead.stage);
  const invite = async () => {
    const w = window.open("", "_blank");
    try { const r = await api.post(`/leads/${lead.id}/invite`); if (w) w.location = waLink(r.data.whatsapp, r.data.text); else window.location.href = waLink(r.data.whatsapp, r.data.text); toast.success("Membership link ready on WhatsApp"); }
    catch (e) { w?.close(); toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2" data-testid="lead-actions">
      <div className="grid grid-cols-2 gap-2">
        <Button asChild variant="outline" className="rounded-full"><a href={`tel:${lead.phone}`} data-testid="lead-call-btn"><Phone className="mr-1 h-4 w-4" />Call</a></Button>
        <Button asChild variant="outline" className="rounded-full"><a href={waLink(lead.whatsapp || lead.phone, `Hi ${lead.name.split(" ")[0]}, this is from Nex Fresh.`)} target="_blank" rel="noreferrer" data-testid="lead-wa-btn"><MessageCircle className="mr-1 h-4 w-4" />WhatsApp</a></Button>
      </div>
      {done && <MemberInfo lead={lead} />}
      {!done && <>
        <Button onClick={invite} className="w-full rounded-full bg-[#D96B43] hover:bg-[#C25A35]" data-testid="lead-invite-btn"><MessageCircle className="mr-1 h-4 w-4" />Send membership link on WhatsApp</Button>
        <MarkPaid lead={lead} onSaved={onSaved} />
      </>}
    </div>
  );
};
